import React, { useRef, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { DatePickerPopover, Dialog, FCWithFragments } from '@modernloop/shared/components';
import {
  IsoTimestamp,
  addDaysAndSetStartOfDay_DEPRECATED,
  assertIsoTimestamp,
  endOfDay,
  formatDate,
  formatTZ,
  getLocalTimezone,
  guessIsoTimestamp,
} from '@modernloop/shared/datetime';
import { PublicError } from '@modernloop/shared/helper-components';
import { logError } from '@modernloop/shared/utils';
import { Alert, FormControl, FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';

import {
  PauseMemberModal_ModuleMemberFragment,
  usePauseMemberModalInterviewModuleMemberUpdateMutation,
} from 'src/generated/mloop-graphql';

import { resetModuleMemberListCache } from 'src/entities/InterviewModuleMember/utils';

type Props = {
  onClose: () => void;
};

type Fragments = {
  moduleMember: PauseMemberModal_ModuleMemberFragment;
};

enum PauseValues {
  Indefinite = 'indefinite',
  TwoWeek = '14',
  OneMonth = '30',
  ThreeMonths = '90',
  Custom = 'custom',
}

const PauseMemberModal: FCWithFragments<Fragments, Props> = ({ onClose, moduleMember }) => {
  const { t } = useTranslation();
  const PauseOptions = [
    {
      label: t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.pauseMember.twoWeeks'),
      value: PauseValues.TwoWeek,
    },
    {
      label: t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.pauseMember.oneMonth'),
      value: PauseValues.OneMonth,
    },
    {
      label: t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.pauseMember.threeMonths'),
      value: PauseValues.ThreeMonths,
    },
    {
      label: t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.pauseMember.indefinitely'),
      value: PauseValues.Indefinite,
    },
  ];
  const newDate = new Date();
  const newDateString = newDate.toISOString();
  const datePickerAnchorEl = useRef(null);
  const timezone = moduleMember.employee?.timezone || getLocalTimezone();

  const [customPauseDate, setCustomPauseDate] = useState<IsoTimestamp>(
    addDaysAndSetStartOfDay_DEPRECATED(assertIsoTimestamp(newDateString), timezone, 1)
  );

  const [pauseValue, setPauseValue] = useState<PauseValues>();
  const [openDatePicker, setDatePickerOpen] = useState<boolean>(false);

  const [updateInterviewMemberPause, { loading, error: memberPauseError }] =
    usePauseMemberModalInterviewModuleMemberUpdateMutation();

  const getParams = () => {
    switch (pauseValue) {
      case PauseValues.Indefinite: {
        return { isPausedIndefinitely: true };
      }
      /** Get the Date according to the timezone in UTC
       * addDays returns date in UTC
       * BE requires in ISO string so converting to ISO string
       */
      case PauseValues.TwoWeek: {
        return {
          pausedUntil: endOfDay(
            addDaysAndSetStartOfDay_DEPRECATED(
              assertIsoTimestamp(newDateString),
              timezone,
              Number(PauseValues.TwoWeek)
            ),
            timezone
          ),
        };
      }
      case PauseValues.OneMonth: {
        return {
          pausedUntil: endOfDay(
            addDaysAndSetStartOfDay_DEPRECATED(
              assertIsoTimestamp(newDateString),
              timezone,
              Number(PauseValues.OneMonth)
            ),
            timezone
          ),
        };
      }
      case PauseValues.ThreeMonths: {
        return {
          pausedUntil: endOfDay(
            addDaysAndSetStartOfDay_DEPRECATED(
              assertIsoTimestamp(newDateString),
              timezone,
              Number(PauseValues.ThreeMonths)
            ),
            timezone
          ),
        };
      }
      case PauseValues.Custom: {
        return { pausedUntil: customPauseDate };
      }
      default: {
        return null;
      }
    }
  };

  const handleUpdate = async () => {
    const params = getParams();
    try {
      await updateInterviewMemberPause({
        variables: {
          input: {
            employeeId: moduleMember.employeeId,
            interviewModuleId: moduleMember.interviewModuleId,
            ...params,
          },
        },
        refetchQueries: ['InterviewModuleMemberSidepanelQuery'],
        awaitRefetchQueries: true,
        update: (cache) => {
          resetModuleMemberListCache({
            cache,
            employeeIds: [moduleMember.employeeId],
            moduleId: moduleMember.interviewModuleId,
          });
        },
      });
      onClose();
    } catch (error) {
      logError(error);
    }
  };

  const getAlertInfoText = () => {
    const params = getParams();

    if (!params) {
      return t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.pauseMember.selectDuration');
    }

    if (params?.isPausedIndefinitely) {
      return t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.pauseMember.resumesNever');
    }

    if (pauseValue === PauseValues.Custom) {
      return t('hiring-dashboard-interviewers:interviewers.pause.resumes', {
        date: formatDate(
          guessIsoTimestamp(addDaysAndSetStartOfDay_DEPRECATED(customPauseDate, timezone, 1)),
          timezone,
          'LLLL d, yyyy'
        ),
      });
    }

    if (params?.pausedUntil) {
      return t('hiring-dashboard-interviewers:interviewers.pause.resumes', {
        date: formatDate(guessIsoTimestamp(params.pausedUntil), timezone, 'LLLL d, yyyy'),
      });
    }

    return '';
  };

  return (
    <Dialog
      decoration={
        <img
          style={{ width: '64px', height: '64px' }}
          src="/static/images/companyDaysOff/umbrella_island.png"
          alt={t('hiring-dashboard-common:common.share.shareLinkAlt')}
        />
      }
      cancelOptions={{ label: t('hiring-dashboard-common:common.cancel'), onClick: onClose }}
      submitOptions={{
        label: t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.pauseMember.pause'),
        onClick: handleUpdate,
        isDisabled: loading,
        isLoading: loading,
      }}
      title={t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.pauseMember.title')}
      subTitle={t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.pauseMember.subtitle')}
      open
      maxWidth="xs"
      onClose={onClose}
    >
      <Typography variant="body1">
        {t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.pauseMember.pauseFor')}
      </Typography>
      <Stack rowGap={1.5}>
        <Stack ml={1.5}>
          <FormControl>
            <RadioGroup
              onChange={(event, value: PauseValues) => {
                setPauseValue(value);
              }}
              name="radio-buttons-pause-group"
            >
              {PauseOptions.map((option) => {
                if (option.value === PauseValues.Custom) {
                  return null;
                }
                return (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio checked={pauseValue === option.value} />}
                    label={option.label}
                  />
                );
              })}

              <FormControlLabel
                key={PauseValues.Custom}
                value={PauseValues.Custom}
                control={<Radio checked={pauseValue === PauseValues.Custom} />}
                ref={datePickerAnchorEl}
                label={
                  <Stack
                    display="flex"
                    columnGap={8}
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography>
                      {t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.pauseMember.customDate')}
                    </Typography>
                    <TextField
                      inputRef={datePickerAnchorEl}
                      variant="outlined"
                      value={customPauseDate ? formatTZ(customPauseDate, timezone, 'MMM d, yyyy') : ''}
                      inputProps={{
                        onClick: () => {
                          setPauseValue(PauseValues.Custom);
                          setDatePickerOpen(true);
                        },
                        readOnly: true,
                      }}
                    />
                  </Stack>
                }
                onChange={() => {
                  setDatePickerOpen(true);
                }}
              />
            </RadioGroup>
          </FormControl>

          <DatePickerPopover
            open={openDatePicker}
            date={customPauseDate}
            timezone={timezone}
            disablePast
            anchorEl={datePickerAnchorEl.current}
            onChange={(date: IsoTimestamp) => {
              setCustomPauseDate(date);
              setDatePickerOpen(false);
            }}
            onClose={() => {
              setDatePickerOpen(false);
            }}
          />
        </Stack>

        <Alert variant="standard" severity="info">
          <Typography variant="body2">{getAlertInfoText()}</Typography>
        </Alert>
      </Stack>
      {memberPauseError && <PublicError error={memberPauseError} />}
    </Dialog>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PauseMemberModalInterviewModuleMemberUpdate = gql`
  mutation PauseMemberModalInterviewModuleMemberUpdate($input: InterviewModuleMemberUpdateInput!) {
    interviewModuleMemberUpdate(input: $input) {
      interviewModuleMember {
        ...pauseMemberModal_moduleMember
      }
    }
  }
`;

PauseMemberModal.fragments = {
  moduleMember: gql`
    fragment pauseMemberModal_moduleMember on InterviewModuleMember {
      id
      employeeId
      interviewModuleId
      isPausedIndefinitely
      isPaused
      pausedUntil
      employee {
        id
        timezone
      }
    }
  `,
};

export default PauseMemberModal;
